import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Target,
  Clock,
  Briefcase,
  Sparkles,
} from "lucide-react";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import ChatWidget from "@/components/landing/ChatWidget";
import WhatsAppButton from "@/components/landing/WhatsAppButton";
import MobileBottomNav from "@/components/landing/MobileBottomNav";
import SERVICES from "@/data/servicesData";
import useSiteConfig from "@/hooks/useSiteConfig";

/**
 * /services — a dedicated long-form page that renders every service as a full
 * section with deliverables, milestones, KPIs, and recent client engagements.
 *
 * Designed for two audiences:
 *   1. Prospects who clicked the "See all 16 in detail" link → deep evaluation
 *   2. Search engines indexing long-tail keywords ("Meta Ads Dubai",
 *      "SaaS growth strategy GCC", etc.). Every service section has a unique
 *      h2, anchor, and rich content body.
 *
 * Reuses Nav, Footer, ChatWidget, WhatsAppButton, MobileBottomNav from the
 * landing page so navigation + branding stay consistent.
 */
export default function ServicesPage() {
  const { config } = useSiteConfig();
  const [activeKey, setActiveKey] = useState(SERVICES[0]?.key);
  const tocRef = useRef(null);

  // Update document title for SEO + browser tab clarity
  useEffect(() => {
    const prev = document.title;
    document.title = "Services · Quantum Star Marketing — Performance, AI, Brand";
    return () => {
      document.title = prev;
    };
  }, []);

  // Active-section tracking for the sticky service nav (TOC)
  useEffect(() => {
    const targets = SERVICES.map((s) => document.getElementById(`svc-${s.key}`)).filter(Boolean);
    if (targets.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const id = visible[0].target.id.replace("svc-", "");
          setActiveKey(id);
          // Auto-scroll the TOC so the active chip is visible
          const chip = tocRef.current?.querySelector(`[data-toc-key="${id}"]`);
          if (chip) {
            chip.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
          }
        }
      },
      { rootMargin: "-25% 0px -55% 0px", threshold: [0, 0.15, 0.4] }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, []);

  return (
    <main className="relative bg-[#050505] text-white selection:bg-cyan-400/20 pb-24 md:pb-0" data-testid="services-page">
      <Nav />

      {/* PAGE HERO */}
      <section className="relative pt-28 sm:pt-36 pb-10 sm:pb-14 overflow-hidden noise" id="top">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 grid-bg radial-fade opacity-40" />
          <motion.div
            animate={{ y: [0, -20, 0], opacity: [0.3, 0.55, 0.3] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 left-[10%] h-72 w-72 rounded-full bg-cyan-500/15 blur-3xl"
          />
          <motion.div
            animate={{ y: [0, 18, 0], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
            className="absolute bottom-0 right-[10%] h-80 w-80 rounded-full bg-purple-600/15 blur-3xl"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-[10px] font-mono-acc uppercase tracking-[0.3em] text-cyan-400 mb-4 inline-flex items-center gap-2">
            <Sparkles size={11} className="text-purple-300" /> / Services Catalogue
          </div>
          <h1 className="font-clash text-[34px] sm:text-5xl lg:text-[80px] font-semibold leading-[1.04] tracking-tight max-w-4xl">
            16 services. <span className="text-gradient-neon">One growth machine.</span>
          </h1>
          <p className="mt-5 sm:mt-7 max-w-2xl text-[15px] sm:text-base md:text-lg text-zinc-400 leading-relaxed">
            Each service below ships as a real engagement — deliverables, 90-day milestones,
            calibrated KPIs, and named client outcomes across UAE, KSA and the wider GCC.
            Tap any service in the navigation to jump straight to it.
          </p>
          <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row gap-3">
            <a
              href={config.contact.bookingUrl || "#contact"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-hero-book"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3.5 text-sm font-medium hover:scale-[1.03] active:scale-[0.98] transition-transform"
            >
              Book a strategy call
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </a>
            <Link
              to="/#audit"
              data-testid="services-hero-audit"
              className="inline-flex items-center justify-center gap-2 rounded-full glass-strong px-6 py-3.5 text-sm font-medium text-white hover:bg-white/10 transition-colors"
            >
              Get a free AI audit instead
            </Link>
          </div>
        </div>
      </section>

      {/* STICKY SERVICE NAV (TOC) */}
      <div className="sticky top-[64px] sm:top-[76px] z-30 bg-[#050505]/85 backdrop-blur-xl border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-3 sm:px-10">
          <div
            ref={tocRef}
            className="flex gap-2 overflow-x-auto py-3 scrollbar-hide [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-testid="services-toc"
          >
            {SERVICES.map((s, i) => (
              <a
                key={s.key}
                href={`#svc-${s.key}`}
                data-toc-key={s.key}
                data-testid={`services-toc-${s.key}`}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] sm:text-xs font-mono-acc tracking-tight transition-colors whitespace-nowrap ${
                  activeKey === s.key
                    ? "bg-white text-black border-white"
                    : "bg-white/[0.03] text-zinc-300 border-white/[0.08] hover:bg-white/[0.08] hover:text-white"
                }`}
              >
                <span className={activeKey === s.key ? "text-zinc-500" : "text-cyan-300"}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                {s.name}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* SERVICE SECTIONS */}
      <div className="relative">
        {SERVICES.map((service, idx) => (
          <ServiceSection key={service.key} service={service} index={idx} bookingUrl={config.contact.bookingUrl} />
        ))}
      </div>

      {/* BOTTOM CTA */}
      <section className="relative py-16 md:py-24 border-t border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-clash text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            Not sure which service <br className="hidden sm:block" />
            <span className="text-gradient-neon">you actually need?</span>
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-zinc-400 text-[15px] sm:text-base leading-relaxed">
            Run a free AI audit on your site — we'll tell you exactly which two or three
            levers will compound revenue fastest, ranked by impact-per-week-of-effort.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/#audit"
              data-testid="services-bottom-cta-audit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3.5 text-sm font-medium hover:scale-[1.03] active:scale-[0.98] transition-transform"
            >
              Run the free AI audit
              <ArrowRight size={16} />
            </Link>
            <a
              href={config.contact.bookingUrl || "#contact"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-bottom-cta-book"
              className="inline-flex items-center justify-center gap-2 rounded-full glass-strong px-6 py-3.5 text-sm font-medium text-white hover:bg-white/10 transition-colors"
            >
              Or talk to a strategist
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <ChatWidget />
      <WhatsAppButton />
      <MobileBottomNav />
    </main>
  );
}

/* ------------------------------ Service section ----------------------------- */

function ServiceSection({ service, index, bookingUrl }) {
  const num = String(index + 1).padStart(2, "0");
  const alternate = index % 2 === 1;
  // Background swap between adjacent sections gives a clean rhythm without
  // requiring a per-section image.
  const sectionBg = alternate ? "bg-[#070709]" : "bg-[#050505]";

  return (
    <section
      id={`svc-${service.key}`}
      data-testid={`svc-section-${service.key}`}
      className={`relative scroll-mt-32 ${sectionBg} border-t border-white/[0.05]`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-24">
        {/* HEADER: number + name + tagline */}
        <div className="flex flex-col gap-3 mb-10 md:mb-14">
          <div className="flex items-baseline gap-4 sm:gap-5">
            <span className="font-mono-acc text-cyan-300 text-sm sm:text-base tracking-tight shrink-0">
              {num} /
            </span>
            <h2 className="font-clash text-3xl sm:text-4xl md:text-6xl font-semibold tracking-tight leading-[1.03]">
              {service.name}
            </h2>
          </div>
          <p className="text-cyan-300/85 text-sm sm:text-base font-mono-acc">{service.tagline}</p>
        </div>

        {/* PROOF CHIPS row */}
        {service.proofPoints && service.proofPoints.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10 md:mb-14">
            {service.proofPoints.slice(0, 3).map((p, i) => (
              <div
                key={i}
                className="rounded-2xl bg-gradient-to-br from-cyan-500/[0.06] via-purple-500/[0.04] to-transparent border border-white/[0.07] p-4 sm:p-5"
              >
                <div className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-zinc-500 mb-2">
                  Proof point {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-clash text-base sm:text-lg font-medium text-white leading-snug">
                  {p}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* OVERVIEW + KPIs row */}
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-10 mb-10 md:mb-14">
          <div className="lg:col-span-2 space-y-5">
            <h3 className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400">
              / Overview
            </h3>
            <p className="text-zinc-200 leading-relaxed text-[15px] sm:text-base">
              {service.overview}
            </p>
          </div>
          <div className="rounded-2xl glass-strong p-5 sm:p-6">
            <div className="flex items-center gap-2 text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-4">
              <Target size={12} /> Calibrated KPIs
            </div>
            <ul className="space-y-3">
              {service.kpis.map((k, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                  <span className="font-mono-acc text-cyan-300 text-[10px] mt-1 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="leading-snug">{k}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* DELIVERABLES + MILESTONES */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-10 mb-10 md:mb-14">
          {/* Deliverables */}
          <div>
            <div className="flex items-center gap-2 text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-5">
              <Briefcase size={12} /> What's inside
            </div>
            <ul className="space-y-2.5">
              {service.deliverables.map((d, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm text-zinc-200 leading-snug"
                >
                  <CheckCircle2 size={14} className="text-cyan-300 mt-0.5 shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Milestones */}
          <div>
            <div className="flex items-center gap-2 text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-5">
              <Clock size={12} /> 90-day shipping plan
            </div>
            <div className="space-y-3">
              {service.milestones.map((m, i) => (
                <div key={i} className="rounded-xl glass p-4 border border-white/[0.05]">
                  <div className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-1.5">
                    {m.day}
                  </div>
                  <div className="text-sm text-zinc-200 leading-snug">{m.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RECENT ENGAGEMENTS */}
        <div className="mb-10 md:mb-12">
          <div className="flex items-center gap-2 text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-5">
            <CheckCircle2 size={12} /> Recent client engagements
          </div>
          <div className="grid md:grid-cols-2 gap-3 md:gap-4">
            {service.industries.map((s, i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-2xl bg-white/[0.02] border border-white/[0.06] p-4 sm:p-5"
              >
                <span className="font-mono-acc text-cyan-300 text-[11px] mt-0.5 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-sm text-zinc-300 leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        </div>

        {/* SECTION CTA */}
        <div className="rounded-2xl bg-gradient-to-br from-purple-500/10 via-cyan-500/[0.06] to-transparent border border-purple-500/20 p-5 sm:p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="font-clash text-lg sm:text-xl font-semibold text-white leading-tight">
              Ready to ship {service.name} like this for your business?
            </div>
            <p className="mt-1 text-sm text-zinc-400">
              30-minute strategy call. No pitch deck. We'll line up what we'd actually do.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Link
              to={`/services/${service.key}`}
              data-testid={`svc-deep-${service.key}`}
              className="inline-flex items-center justify-center gap-2 rounded-full glass-strong px-5 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              Full {service.name} page →
            </Link>
            <a
              href={bookingUrl || "#contact"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`svc-cta-${service.key}`}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-5 py-3 text-sm font-medium hover:scale-[1.03] active:scale-[0.98] transition-transform whitespace-nowrap"
            >
              Book the call
              <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
